import axiosInstance from './axiosInstance';

import type { Response } from './axiosInstance';

/** How a user who does not yet have access obtains it. */
export type CourseType = 'course' | 'preregister';

/** Tier of a pre-registration for a paid course. */
export type PreRegisterTier = 'normal' | 'premium';

export type Course = {
    id: number;
    title: string;
    description: string;
    /** The user may open the course. */
    available: boolean;
    /** An application for a free course is waiting for an admin. */
    applied?: boolean;
    /** The user has pre-registered for a paid course. */
    pre_registered?: boolean;
    /** Which tier they pre-registered for, when they have. */
    tier?: PreRegisterTier;
    /** Course/paid-course access model; the Lab backend does not send it. */
    type?: CourseType;
    url: string;
}

/**
 * The backend answers "you already did this" with 409. That is a normal
 * outcome the page shows a message for, not a failure, so the status is
 * carried out of the catch rather than collapsed into undefined.
 */
export type CourseActionResult = {
    ok: boolean;
    message: string;
    status?: number;
};

export const getCourses = async (): Promise<Response<Course[]> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<Course[]>>('/courses', {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        console.error('Failed to get courses', e);
        return undefined;
    }
}

/** Apply for a free course. An administrator reviews the application. */
export const applyForCourse = async (courseId: number): Promise<CourseActionResult> => {
    try {
        const response = await axiosInstance.post<Response<null>>(`/courses/${courseId}/apply`);
        return { ok: !!response.data?.ok, message: response.data?.message ?? '', status: response.status };
    } catch (e: any) {
        console.error('Failed to apply for course', e);
        return {
            ok: false,
            message: e?.response?.data?.message ?? '',
            status: e?.response?.status,
        };
    }
}

/** Register interest in a paid course. This grants no access by itself. */
export const preRegisterCourse = async (
    courseId: number,
    tier: PreRegisterTier,
): Promise<CourseActionResult> => {
    try {
        const response = await axiosInstance.post<Response<null>>(
            `/courses/${courseId}/preregister`,
            { tier },
        );
        return { ok: !!response.data?.ok, message: response.data?.message ?? '', status: response.status };
    } catch (e: any) {
        console.error('Failed to pre-register for course', e);
        return {
            ok: false,
            message: e?.response?.data?.message ?? '',
            status: e?.response?.status,
        };
    }
}